import { lumi } from '@/lib/lumi'
import {Loader2, Lock, Mail, X} from 'lucide-react'
import { useEffect, useState } from 'react'

type Mode = 'signin' | 'signup' | 'reset'

interface AuthModalProps {
  open: boolean
  onClose: () => void
}

function AuthModal({ open, onClose }: AuthModalProps) {
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)

  useEffect(() => {
    if (countdown <= 0)
      return
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  useEffect(() => {
    if (open) {
      setError('')
      setOtp('')
      setPassword('')
    }
  }, [open])

  if (!open)
    return null

  const switchMode = (nextMode: Mode) => {
    setMode(nextMode)
    setError('')
    setOtp('')
  }

  const handleSendOtp = async () => {
    if (!email) {
      setError('Please enter your email first.')
      return
    }
    try {
      await lumi.auth.sendOtp({ email, type: mode === 'reset' ? 'resetPassword' : 'register' })
      setCountdown(60)
    }
    catch (e: any) {
      setError(e?.message || 'Failed to send verification code.')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'signin') {
        await lumi.auth.signInWithPassword({ email, password })
      }
      else if (mode === 'signup') {
        await lumi.auth.signUpWithPassword({ email, password, otp })
      }
      else {
        await lumi.auth.resetPassword({ email, newPassword: password, otp })
      }
      onClose()
    }
    catch (e: any) {
      setError(e?.message || 'Authentication failed. Please try again.')
    }
    finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-2xl border border-amber-500/20 bg-[#0e0c0a] p-6 shadow-2xl shadow-black/60 sm:p-8">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-white/5 hover:text-amber-400"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        <h2 className="font-serif text-2xl font-bold text-white">
          {mode === 'signin' && 'Welcome Back'}
          {mode === 'signup' && 'Create Your Account'}
          {mode === 'reset' && 'Reset Your Password'}
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          {mode === 'signin' && 'Sign in to save and revisit your tattoo & removal estimates.'}
          {mode === 'signup' && 'Register to keep every estimate saved to your account.'}
          {mode === 'reset' && 'Verify your email to set a new password.'}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="auth-email" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-300">
              <Mail className="h-4 w-4 text-amber-400" aria-hidden="true" />
              Email
            </label>
            <input
              id="auth-email"
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
              placeholder="you@example.com"
            />
          </div>

          {(mode === 'signup' || mode === 'reset') && (
            <div>
              <label htmlFor="auth-otp" className="mb-2 block text-sm font-medium text-slate-300">Verification Code</label>
              <div className="flex gap-2">
                <input
                  id="auth-otp"
                  type="text"
                  required
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                  placeholder="6-digit code"
                />
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={countdown > 0}
                  className="shrink-0 rounded-lg border border-amber-400/40 px-4 py-3 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {countdown > 0 ? `${countdown}s` : 'Send'}
                </button>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="auth-password" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-300">
              <Lock className="h-4 w-4 text-amber-400" aria-hidden="true" />
              {mode === 'reset' ? 'New Password' : 'Password'}
            </label>
            <input
              id="auth-password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-3 text-sm font-semibold text-black shadow-lg shadow-amber-500/20 transition-transform hover:scale-[1.02] hover:bg-amber-400 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {mode === 'signin' && 'Sign In'}
            {mode === 'signup' && 'Create Account'}
            {mode === 'reset' && 'Reset Password'}
          </button>

          {mode !== 'reset' && (
            <button
              type="button"
              onClick={() => lumi.auth.signInWithOAuth({ provider: 'google' })}
              className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-medium text-slate-200 transition-colors hover:bg-white/10"
            >
              Continue with Google
            </button>
          )}
        </form>

        <div className="mt-5 flex flex-col gap-2 text-center text-sm">
          {mode === 'signin' && (
            <>
              <button type="button" onClick={() => switchMode('reset')} className="text-slate-400 hover:text-amber-400">
                Forgot password?
              </button>
              <span className="text-slate-500">
                No account?
                {' '}
                <button type="button" onClick={() => switchMode('signup')} className="font-medium text-amber-400 hover:underline">
                  Sign up
                </button>
              </span>
            </>
          )}
          {mode === 'signup' && (
            <span className="text-slate-500">
              Already have an account?
              {' '}
              <button type="button" onClick={() => switchMode('signin')} className="font-medium text-amber-400 hover:underline">
                Sign in
              </button>
            </span>
          )}
          {mode === 'reset' && (
            <button type="button" onClick={() => switchMode('signin')} className="text-slate-400 hover:text-amber-400">
              Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuthModal
